import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { Card } from '../../components/card';
import { Symbol } from '../../components/symbol';
import { SkeletonRows } from '../../components/skeleton';
import { Toggle } from '../../components/toggle';
import { EmptyState } from '../../components/empty-state';
import { TableSearch, useTableSearch } from '../../components/table-search';
import { useConfirm } from '../../components/confirm-dialog';
import { useAuthStore } from '../../store/auth-store';
import { mediaSrc } from '../../api/media';
import {
  CrudDrawer,
  TextField,
  TextAreaField,
  CheckField,
  DeleteButton,
  extractErr,
} from '../../components/crud-drawer';

interface Brand {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  description: string | null;
  isActive: boolean;
}

function slugify(name: string): string {
  return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

const EMPTY = { name: '', slug: '', logoUrl: '', description: '', isActive: true };

export function BrandsTab() {
  const canWrite = useAuthStore((s) => s.hasScope)('catalog.brand.write');

  const [rows, setRows] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<Brand | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const { confirm } = useConfirm();
  // Client-side: the API returns every brand. Matches each column shown.
  const { query, setQuery, filtered } = useTableSearch(rows, (r) => [
    r.name, r.description, r.slug, r.isActive ? 'Active' : 'Inactive',
  ]);
  const [slugTouched, setSlugTouched] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get<Brand[]>('/admin/brands');
      setRows(data);
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const openCreate = () => {
    setEditing(null); setCreating(true); setSlugTouched(false); setForm(EMPTY);
  };

  const openEdit = (row: Brand) => {
    setCreating(false); setEditing(row); setSlugTouched(true);
    setForm({
      name: row.name,
      slug: row.slug,
      logoUrl: row.logoUrl ?? '',
      description: row.description ?? '',
      isActive: row.isActive,
    });
  };

  const close = () => { setEditing(null); setCreating(false); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    const slug = (form.slug || slugify(form.name)).trim();
    if (!/^[a-z0-9-]+$/.test(slug)) {
      toast.error('Slug may only contain lowercase letters, numbers and hyphens');
      return;
    }
    const body = {
      name: form.name.trim(),
      slug,
      logoUrl: form.logoUrl.trim() || null,
      description: form.description.trim() || null,
      isActive: form.isActive,
    };
    setBusy(true);
    try {
      if (creating) {
        await api.post('/admin/brands', body);
        toast.success('Brand created');
      } else if (editing) {
        await api.put(`/admin/brands/${editing.id}`, body);
        toast.success('Brand updated');
      }
      close();
      await load();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!editing) return;
    const ok = await confirm({
      title: `Delete "${editing.name}"?`,
      description: 'This can\'t be undone, and it fails if any product still references the brand. Deactivate it instead to hide it from new listings.',
      confirmLabel: 'Delete brand',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/admin/brands/${editing.id}`);
      toast.success('Brand deleted');
      close();
      await load();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (row: Brand, next: boolean) => {
    // Switching off hides it at once, so ask first; switching back on is
    // harmless and stays one click.
    if (!next) {
      const ok = await confirm({
        title: `Deactivate "${row.name}"?`,
        description: 'It disappears from the seller\'s product form and the shopper\'s brand filter. You can switch it back on any time.',
        confirmLabel: 'Deactivate brand',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setRows((cur) => cur.map((r) => (r.id === row.id ? { ...r, isActive: next } : r)));
    try {
      await api.put(`/admin/brands/${row.id}`, { isActive: next });
    } catch (err) {
      toast.error(extractErr(err));
      setRows((cur) => cur.map((r) => (r.id === row.id ? { ...r, isActive: !next } : r)));
    }
  };

  return (
    <>
      <Card padding="none">
        <div className="flex items-center justify-between border-b border-surface-line px-5 py-4">
          <div>
            <h2 className="text-[16px] font-semibold text-ink-900">Brands</h2>
            <p className="mt-0.5 text-[13.5px] text-ink-500">
              Optional on a product. Doubles as a customer-facing filter.
            </p>
          </div>
          {canWrite && (
            <button
              type="button"
              onClick={openCreate}
              className="btn btn-primary btn-sm shrink-0"
            >
              <Symbol name="add" size={16} /> Add brand
            </button>
          )}
        </div>

        {!loading && rows.length > 0 && (
          <div className="border-b border-surface-line px-5 py-3">
            <TableSearch
              value={query}
              onChange={setQuery}
              placeholder="Search by brand, description, slug or status"
              resultCount={filtered.length}
            />
          </div>
        )}

        {loading ? (
          <SkeletonRows cols={4} />
        ) : rows.length === 0 ? (
          <EmptyState
            bare
            icon="sell"
            title="No brands yet"
            body="Brands you add appear here and in the seller's product form."
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            bare
            icon="search_off"
            title={`No matches for "${query.trim()}"`}
            body="Try a brand name, part of its description, its slug, or Active / Inactive."
            action={
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setQuery('')}>
                Clear search
              </button>
            }
          />
        ) : (
          <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Brand</th>
                <th>Slug</th>
                <th className="!text-right">Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className={r.isActive ? '' : 'bg-surface-sunk/60'}>
                  <td>
                    <div className="flex items-center gap-2.5">
                      {r.logoUrl ? (
                        <img src={mediaSrc(r.logoUrl)} alt="" className="h-7 w-7 rounded-xs object-contain" />
                      ) : (
                        <div className="grid h-7 w-7 place-items-center rounded-xs bg-surface-2">
                          <Symbol name="sell" size={14} className="text-ink-400" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-medium text-ink-900">{r.name}</div>
                        {r.description && (
                          <div className="truncate text-[12.5px] text-ink-500">{r.description}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="sku text-ink-500">{r.slug}</td>
                  <td>
                    <div className="flex justify-end">
                      <Toggle
                        checked={r.isActive}
                        disabled={!canWrite}
                        aria-label={`${r.isActive ? 'Deactivate' : 'Activate'} ${r.name}`}
                        onChange={(v) => void toggleActive(r, v)}
                      />
                    </div>
                  </td>
                  <td className="text-right">
                    {canWrite && (
                      <button
                        type="button"
                        onClick={() => openEdit(r)}
                        className="btn btn-ghost btn-sm"
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      <CrudDrawer
        open={creating || editing !== null}
        title={creating ? 'New brand' : `Edit ${editing?.name ?? ''}`}
        busy={busy}
        onClose={close}
        onSubmit={() => void save()}
        secondaryAction={editing ? <DeleteButton onClick={() => void remove()} disabled={busy} /> : undefined}
      >
        <TextField
          label="Name"
          value={form.name}
          onChange={(v) => setForm((f) => ({ ...f, name: v, slug: slugTouched ? f.slug : slugify(v) }))}
          placeholder="Fabindia"
          required
        />
        <TextField
          label="Slug"
          value={form.slug}
          onChange={(v) => { setSlugTouched(true); setForm((f) => ({ ...f, slug: v })); }}
          placeholder="fabindia"
          mono
          required
          hint="Used in the brand filter URL."
        />
        <TextField
          label="Logo URL"
          value={form.logoUrl}
          onChange={(v) => setForm((f) => ({ ...f, logoUrl: v }))}
          placeholder="https://…"
        />
        <TextAreaField
          label="Description"
          value={form.description}
          onChange={(v) => setForm((f) => ({ ...f, description: v }))}
          placeholder="Optional blurb shown on the brand page"
        />
        <CheckField
          label="Active"
          hint="Inactive brands disappear from the seller's product form and the shopper's filter."
          checked={form.isActive}
          onChange={(v) => setForm((f) => ({ ...f, isActive: v }))}
        />
      </CrudDrawer>
    </>
  );
}
